if (!customElements.get('store-appointment')) {
  customElements.define(
    'store-appointment',
    class StoreAppointment extends HTMLElement {
      get info() {
        return this._info;
      }

      set info(val) {
        this._info = val;
      }

      /**
       * @returns {string | number | null}
       */
      get timeId() {
        return this._timeId;
      }

      set timeId(val) {
        this._timeId = val;
      }

      get hasBeenCompleted() {
        return this._hasBeenCompleted;
      }

      set hasBeenCompleted(val) {
        this._hasBeenCompleted = val;

        val
          ? this.elements.submit?.removeAttribute('disabled')
          : this.elements.submit?.setAttribute('disabled', 'true');

        val
          ? this.elements.submit?.classList.remove('button--disabled')
          : this.elements.submit?.classList.add('button--disabled');
      }

      /**
       * @returns {number | null}
       */
      get examTypeId() {
        return this._examTypeId;
      }

      set examTypeId(val) {
        this._examTypeId = val;

        this._dayData = [];
        this._timeData = [];
      }

      /**
       * @returns {number | null}
       */
      get locationId() {
        return this._locationId;
      }

      set locationId(val) {
        this._locationId = val;

        if (!this.location) {
          this.resetLocation();

          return;
        }

        this.setLocation();
      }

      constructor() {
        super();
        this.elements.examTypes?.forEach((el) => (el.checked = false));
        this._accessKey = window.atob('Rlc3bU52LUR6VE1KIWxMSUk/bWF1c1c5dkF1TUdxZnZ3Ti1XWWthMQ==');
        this._locationData = [];
        this._dayData = [];
        this._timeData = [];
        this._locationId = null;
        this._timeId = null;
        this._dayId = null;
        this._hasBeenCompleted = false;
        this._examTypeId = null;
        this._selectedSystem = null; // 'new' (PMS) or 'old' (Sylius)
        this._info = { name: '', email: '', phone: '', dateOfBirth: '', address: '', bookingRecipient: 'SELF' };

        this.elements.examTypes?.forEach((el) => {
          el.addEventListener('change', this.handleExamTypeChange.bind(this));
        });

        // Listen for booking recipient change
        this.elements.bookingRecipients?.forEach((el) => {
          el.addEventListener('change', this.handleBookingRecipientChange.bind(this));
        });

        this.elements.location?.addEventListener('change', this.handleLocationChange.bind(this));
        this.addEventListener('dateselect', this.handleDaySelection.bind(this));
        this.elements.time?.addEventListener('change', this.handleTimeChange.bind(this));
        this.elements.infoContainer?.addEventListener('input', this.handleInfoChange.bind(this));
        this.elements.submit?.addEventListener('click', this.handleSubmission.bind(this));
      }

      connectedCallback() {
        // Watch for step 4 (booking recipient) to become enabled, then auto-validate
        const step4 = this.querySelector('[js-form-wizard-step]:has([data-store-appointment-el="booking-recipient"])');
        if (step4) {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
                if (!step4.hasAttribute('disabled')) {
                  // Step 4 just became enabled - dispatch change on pre-selected radio
                  const checkedRecipient = step4.querySelector(
                    '[data-store-appointment-el="booking-recipient"]:checked',
                  );
                  if (checkedRecipient) {
                    checkedRecipient.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                  observer.disconnect();
                }
              }
            });
          });
          observer.observe(step4, { attributes: true });
        }

        this.getLocations().then((r) => {
          this._locationData = r.channels;

          /** @type {{ label: string }[]} */
          let options = this.getLocationOptionsFromResponse(r);

          if (this.currentMarket === 'us') {
            options = options.filter((option) =>
              this.usStores.some(/** @param {string} store */ (store) => store === option.label),
            );
          } else {
            options = options.filter((option) => !this.usStores.includes(option.label));
          }

          this.renderOptions(this.elements.location, options);

          const locationExists = options.some((option) => option.label === this.getAttribute('current'));
          this.elements.location?.removeAttribute('disabled');

          if (locationExists && this.elements.location) {
            this.elements.location.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      /**
       * @param {Event} e
       */
      async handleExamTypeChange(e) {
        if (!e.target) return;

        this.resetTime();
        this.resetDay();

        this.hasBeenCompleted = false;
        this.examTypeId = parseInt(e.target.value, 10);
        this._dayData = await this.getLocationDays(this.locationId, this.examTypeId);

        this.elements.datePicker.setAvailableDays(
          this._dayData.map((dayData) => this.elements.datePicker.getDateFromString(dayData.date)),
        );
        this.elements.datePicker._renderCalendar();
        this.elements.timeContainer.hidden = true;
      }

      /**
       * @param {Event} e
       */
      handleTimeChange(e) {
        this.hasBeenCompleted = false;
        // Support both numeric IDs (old Sylius system) and UUID strings (new PMS system)
        this.timeId = e.target.value;
      }

      /**
       * @param {Event} e
       */
      handleBookingRecipientChange(e) {
        if (!e.target) return;

        this.info.bookingRecipient = e.target.value;

        // If "SELF" selected and customer is logged in, pre-fill name/email/address from customer data
        if (e.target.value === 'SELF' && this.customer.isLoggedIn) {
          const nameInput = this.querySelector('[name="name"]');
          const emailInput = this.querySelector('[name="email"]');
          const addressInput = this.querySelector('[name="address"]');

          // Pre-fill name from customer
          if (nameInput && this.customer.name) {
            nameInput.value = this.customer.name;
            this.info.name = this.customer.name;
          }

          // Pre-fill email from customer
          if (emailInput && this.customer.email) {
            emailInput.value = this.customer.email;
            this.info.email = this.customer.email;
          }

          // Pre-fill address from customer's default address if available
          if (addressInput && this.customer.default_address) {
            const addr = this.customer.default_address;
            const addressParts = [addr.address1, addr.address2, addr.city, addr.zip].filter(Boolean);
            const fullAddress = addressParts.join(', ');
            addressInput.value = fullAddress;
            this.info.address = fullAddress;
          }

          // Trigger input events to update validation state
          if (nameInput) nameInput.dispatchEvent(new Event('input', { bubbles: true }));
          if (emailInput) emailInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      /**
       * @param {Event} e
       */
      handleInfoChange(e) {
        const fields = ['email', 'name', 'phone'];
        const optionalFields = ['dateOfBirth', 'address'];

        const isTrackedField = fields.includes(e.target.name) || optionalFields.includes(e.target.name);
        if (!isTrackedField) return;

        this.info = {
          ...this.info,
          [e.target.name]: e.target.value,
        };

        const phonePattern = /^(?:\+|00)?[0-9()\-\s\.]{7,20}$/;
        if (e.target.name === 'phone') {
          if (phonePattern.test(e.target.value)) {
            this.info.phone = e.target.value;
          }
        }

        const emailPattern = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
        if (e.target.name === 'email') {
          if (emailPattern.test(e.target.value)) {
            this.info.email = e.target.value;
          }
        }

        if (e.target.name === 'name') {
          this.info.name = e.target.value;
        }

        // Handle optional fields (DOB and address)
        if (e.target.name === 'dateOfBirth') {
          this.info.dateOfBirth = e.target.value;
        }

        if (e.target.name === 'address') {
          this.info.address = e.target.value;
        }

        if (this.customer.isLoggedIn) {
          const loggedInFields = fields.filter((field) => ['phone'].includes(field));

          this.hasBeenCompleted = loggedInFields.every((field) => {
            if (field === 'email' && !emailPattern.test(this.info[field])) {
              return false;
            }

            if (field === 'phone' && !phonePattern.test(this.info[field])) {
              return false;
            }

            return this.info[field] !== '';
          });

          return;
        }

        this.hasBeenCompleted = fields.every((field) => {
          if (field === 'email' && !emailPattern.test(this.info[field])) {
            return false;
          }

          if (field === 'phone' && !phonePattern.test(this.info[field])) {
            return false;
          }

          return this.info[field] !== '';
        });
      }

      /**
       *
       * @param {Event} e
       */
      async handleSubmission(e) {
        e.preventDefault();

        this.elements.submit?.setAttribute('disabled', 'true');
        this.loading = true;

        const isNewSystem = this._selectedSystem === 'new';

        // Build request body based on system type
        const body = {
          id: window.theme.customer.id,
          length: this._examTypeId,
          phone: this.info.phone,
        };

        if (isNewSystem) {
          // NEW SYSTEM (PMS) - slot is UUID string, include PMS-specific fields
          body.slot = this._timeId;
          body.bookingRecipient = this.info.bookingRecipient || 'SELF';

          if (this.info.dateOfBirth) {
            body.dateOfBirth = this.info.dateOfBirth;
          }

          if (this.info.address) {
            body.address = this.info.address;
          }

          // Include name/email if not logged in OR booking for someone else
          if (this.customer.isLoggedIn === false || this.info.bookingRecipient === 'OTHER') {
            body.name = this.info.name;
            body.email = this.info.email;
          }
        } else {
          // OLD SYSTEM (Sylius) - only accepts: id, email, name, slot (int), length (int), phone
          body.slot = parseInt(this._timeId, 10);

          // Always include name/email for Sylius
          if (this.customer.isLoggedIn) {
            body.name = this.customer.name;
            body.email = this.customer.email;
          } else {
            body.name = this.info.name;
            body.email = this.info.email;
          }
        }

        const queryString = window.location.search;

        const urlParams = new URLSearchParams(queryString);
        const source = urlParams.get('source');

        // Log the window url
        var xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function () {
          if (this.readyState == 4 && this.status == 200) {
            // Typical action to be performed when the document is ready:
            console.log(xhttp.responseText);
          }
        };
        xhttp.open('GET', 'https://cubittsadmin.com/logeyetestbooking?source=' + source, true);
        xhttp.send();

        if (source == 'bakery') {
          body.name = body.name + '(BAKERY PROM)';
        }

        gtag('event', 'form_submit', {
          form_name: 'eye_exam',
          form_options: source,
        });

        await window.CubittsAPI.eyeExamBooking(body, this.locationId, this._dayId);

        this.loading = false;

        this.elements.form?.setAttribute('aria-hidden', 'true');
        this.elements.confirmation?.removeAttribute('aria-hidden');
      }

      getLocationOptionsFromResponse(response) {
        return response.channels.map((channel) => ({
          value: channel.id,
          label: channel.longName,
          selected: channel.longName === (this.getAttribute('current') || ''),
          examTypes: channel.offeredFrontBookingTypes,
          system: channel._system, // old or new
        }));
      }

      renderOptions(el, options) {
        Array.from(el.options).forEach((option, index) => {
          if (index === 0) return;

          // We always want to remove the 2nd index because the list shrinks as we remove it we can't remove the index it was at when the loop started
          el.options.remove(1);
        });

        options.forEach(({ label, value, selected = false, examTypes, system, dataset = {} } = {}) => {
          const option = new Option(label, value, false, selected);
          option.setAttribute('data-exam-types', examTypes);
          option.setAttribute('data-system', system || 'old'); // default to old if not specified
          option.setAttribute('data-label', label.toLowerCase());
          el.options.add(option);
        });

        // Auto select the first store if there is only one available
        if (options.length === 1) {
          this.changeLocation(el, 1);
        }
      }

      /**
       * @param {HTMLSelectElement} select
       * @param {number} index
       */
      changeLocation(select, index) {
        select.selectedIndex = index;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /**
       * Check if an exam type should be visible for a given store
       * @param {HTMLElement} examTypeElement - The exam type selector element
       * @param {string} storeName - The store name
       * @returns {boolean} - Whether the exam type should be visible
       */
      isExamTypeVisibleForStore(examTypeElement, storeName) {
        const visibleStoresAttr = examTypeElement.dataset.visibleStores;
        const hiddenStoresAttr = examTypeElement.dataset.hiddenStores;

        if (!visibleStoresAttr && !hiddenStoresAttr) {
          return true;
        }

        const visibleStores = visibleStoresAttr ? JSON.parse(visibleStoresAttr) : [];
        const hiddenStores = hiddenStoresAttr ? JSON.parse(hiddenStoresAttr) : [];

        if (hiddenStores.length > 0 && hiddenStores.includes(storeName)) {
          return false;
        }

        if (visibleStores.length > 0 && !visibleStores.includes(storeName)) {
          return false;
        }

        return true;
      }

      /**
       * @param {Event} e
       */
      handleLocationChange(e) {
        const selectedOption = e.target.options[e.target.selectedIndex];
        const availableExamTypes = selectedOption.getAttribute('data-exam-types')?.split(',');
        const system = selectedOption.getAttribute('data-system');
        const selectedStoreName = selectedOption.dataset.label;

        // Store selected system and update field visibility (only if changed)
        const newSystem = system || 'old';
        if (this._selectedSystem !== newSystem) {
          this._selectedSystem = newSystem;
          this.updateFieldVisibility();
        }

        if (!availableExamTypes) return;

        this.elements.examTypesSelector.forEach((el) => {
          const isAvailableExamType = availableExamTypes.includes(el.dataset.id);
          const isVisibleForStore = this.isExamTypeVisibleForStore(el, selectedStoreName);

          el.style.display = isAvailableExamType && isVisibleForStore ? 'block' : 'none';
        });

        this.hasBeenCompleted = false;
        this.locationId = parseInt(e.target?.value, 10);
        this._dayData = [];
        this._timeData = [];
        this.elements.timeContainer.hidden = true;
        this.resetTime();
        this.resetDay();

        const visibleExamTypes = Array.from(this.elements.examTypesSelector).filter(
          (el) => el.style.display !== 'none',
        ).length;

        if (visibleExamTypes === 1) {
          this.setExamType();
        } else {
          this.resetExamType();
        }
      }

      /**
       * Show/hide PMS-specific fields based on selected booking system
       * Step 4 (booking recipient) and DOB/Address fields are only for NEW system (PMS)
       */
      updateFieldVisibility() {
        const isNewSystem = this._selectedSystem === 'new';

        // Get all form step containers
        const allStepContainers = this.querySelectorAll('[js-form-wizard-step-container]');

        // Step 4 container is the booking recipient step (index 3, 0-based)
        const step4Container = allStepContainers[3];
        const step4 = step4Container?.querySelector('[js-form-wizard-step]');

        if (step4Container && step4) {
          if (isNewSystem) {
            step4Container.style.display = '';
            step4.removeAttribute('aria-hidden');
          } else {
            step4Container.style.display = 'none';
            step4.setAttribute('aria-hidden', 'true');
          }
        }

        // DOB and Address fields in Step 5 (Personal details)
        const dobField = this.querySelector('#CustomerDateOfBirth')?.closest('.store-appointment__input');
        const addressField = this.querySelector('#CustomerAddress')?.closest('.store-appointment__input');

        if (dobField) dobField.style.display = isNewSystem ? '' : 'none';
        if (addressField) addressField.style.display = isNewSystem ? '' : 'none';

        // Update step numbering (4 steps for old system, 5 for new)
        const totalSteps = isNewSystem ? 5 : 4;
        let visibleStepNumber = 0;

        allStepContainers.forEach((container, index) => {
          const step = container.querySelector('[js-form-wizard-step]');
          const stepLabel = step?.querySelector('.form-wizard__step-header h2');

          // Skip hidden containers
          if (container.style.display === 'none') return;

          visibleStepNumber++;

          if (stepLabel) {
            stepLabel.textContent = `Step ${visibleStepNumber} of ${totalSteps}`;
          }
        });
      }

      /**
       *
       * @param {Event} e
       */
      async handleDaySelection(e) {
        this.hasBeenCompleted = false;
        // $YEAR$-$MONTH$-$DAY$
        const [year, month, day] = e.target.value.split('-');
        const dayDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
        const dayData = this._dayData.find((d) => {
          const [year, month, day] = d.date.split('-');
          const dD = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));

          return dD.getTime() === dayDate.getTime();
        });

        if (!dayData) {
          console.error('[StoreAppointment] No availability data for selected day', e.target.value);

          return;
        }

        // A cut-over practice serves pre-cutover days from Sylius and the rest from PMS,
        // so the system follows the day, not the practice. The extra PMS fields live in a
        // later step, so switching here still happens before the customer reaches them.
        this._dayId = dayData.id;
        const daySystem = dayData._system || this._selectedSystem;

        if (daySystem && this._selectedSystem !== daySystem) {
          this._selectedSystem = daySystem;
          this.updateFieldVisibility();
        }

        this._timeData = await this.getTime(dayData.id, this.examTypeId, this.locationId);

        this.renderOptions(
          this.elements.time,
          this._timeData
            .map((time) => ({
              label: time.slot,
              value: time.id,
            }))
            .sort((dateA, dateB) => {
              if (dateA.label > dateB.label) {
                return 1;
              } else if (dateA.label < dateB.label) {
                return -1;
              }

              return 0;
            })
            .map((opt) => ({
              label: this.show12HourTime ? this.formatTo12Hour(opt.label) : opt.label,
              value: opt.value,
            })),
        );

        this.resetTime();

        this.elements.timeContainer.hidden = false;
      }

      /**
       * @param {Number} examTypeId
       */
      async getLocations() {
        this.loading = true;

        const res = await window.CubittsAPI.eyeExamLocationsAll();

        this.loading = false;

        return res;
      }

      async getLocationDays(locationId, examTypeId) {
        this.loading = true;

        try {
          return await window.CubittsAPI.eyeExamLocationDays(locationId, examTypeId);
        } catch (error) {
          // An empty calendar is wrong, but showing days we cannot honour is worse
          console.error('[StoreAppointment] Failed to load available days', error);

          return [];
        } finally {
          this.loading = false;
        }
      }

      /**
       * @param dayId {number}
       * @param examTypeId {number}
       * @param locationId {number}
       */
      async getTime(dayId, examTypeId, locationId) {
        this.loading = true;

        const res = /** @type {{ slot: string }[]} */ (
          (await window.CubittsAPI?.eyeExamTimes(dayId, examTypeId, locationId)) ?? []
        );

        this.loading = false;

        return res;
      }

      async setLocation() {
        if (!this.location.longName || !this.location.street) return;

        this.elements.storeLine.hidden = false;
        this.elements.storeTitle.textContent = this.location.longName;
        this.elements.storeAddress.textContent = this.location.street;
        this.elements.storePhone.textContent = this.location.phone;
      }

      setExamType() {
        this._dayData = [];

        const visibleExamType = Array.from(this.elements.examTypes).find((el) => el.style.display !== 'none');

        if (visibleExamType) {
          visibleExamType.checked = true;
          visibleExamType.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      resetExamType() {
        this._dayData = [];

        this.elements.examTypes.forEach((el) => {
          el.checked = false;
        });
      }

      resetLocation() {
        this._dayData = [];

        this.elements.storeLine.hidden = true;
        this.elements.storeTitle.textContent = '';
        this.elements.storeAddress.textContent = '';
        this.elements.storePhone.textContent = '';

        this.elements.timeAndLocationContainer.hidden = true;
        this.elements.datePicker._hasSelection = false;
        this.elements.timeContainer.hidden = true;
      }

      resetTime() {
        this.elements.time.value = '-1';
        this.elements.time.dispatchEvent(new Event('change', { bubbles: true }));
      }

      resetDay() {
        this._dayId = null;
        document.querySelector('.calendar__day--selected')?.classList.remove('calendar__day--selected');
      }

      /**
       * @param time {string}
       */
      formatTo12Hour(time) {
        let [h, m] = time.split(':').map(Number);
        const suffix = h >= 12 ? 'PM' : 'AM';

        h = ((h + 11) % 12) + 1;

        return `${h}:${m.toString().padStart(2, '0')} ${suffix}`;
      }

      get show12HourTime() {
        return this.currentMarket ? this.countriesUsing12HourTime.includes(this.currentMarket) : false;
      }

      get countriesUsing12HourTime() {
        return ['us', 'ca', 'au', 'nz', 'za', 'in', 'pk', 'bd', 'mx', 'ph'];
      }

      get location() {
        return this._locationData.find((l) => l.id === this.locationId);
      }

      get customer() {
        return window.theme.customer;
      }

      /**
       * @returns {boolean | undefined}
       */
      get loading() {
        return this._loading;
      }

      set loading(val) {
        this._loading = val;
      }

      get currentMarket() {
        return this.dataset.currentMarket;
      }

      get usStores() {
        return JSON.parse(this.dataset.usStores || '{}');
      }

      get elements() {
        return {
          /**
           * @type {NodeListOf<HTMLInputElement>}
           */
          examTypes: this.querySelectorAll('[data-store-appointment-el="exam-type"]'),
          /**
           * @type {NodeListOf<HTMLElement>}
           */
          examTypesSelector: this.querySelectorAll('[data-store-appointment-el="exam-type-selector"]'),
          location: this.querySelector('[data-store-appointment-el="location"]'),
          time: this.querySelector('[data-store-appointment-el="time"]'),
          datePicker: this.querySelector('[data-store-appointment-el="date-picker"]'),
          timeContainer: this.querySelector('[data-store-appointment-el="time-container"]'),
          confirmation: this.querySelector('[data-store-appointment-el="confirmation"]'),
          loading: this.querySelector('[data-store-appointment-el="loading"]'),
          submit: this.querySelector('[data-store-appointment-el="submit"]'),
          submitText: this.querySelector('[data-store-appointment-el="submit-text"]'),
          infoContainer: this.querySelector('[data-store-appointment-el="info-container"]'),
          storeLine: this.querySelector('[data-store-appointment-el="store-line"]'),
          storeTitle: this.querySelector('[data-store-appointment-el="store-title"]'),
          storeAddress: this.querySelector('[data-store-appointment-el="store-address"]'),
          storePhone: this.querySelector('[data-store-appointment-el="store-phone"]'),
          timeAndLocationContainer: this.querySelector('[data-store-appointment-el="time-and-location-container"]'),
          form: this.querySelector('.store-appointment__form'),
          /**
           * @type {NodeListOf<HTMLInputElement>}
           */
          bookingRecipients: this.querySelectorAll('[data-store-appointment-el="booking-recipient"]'),
        };
      }
    },
  );
}
